import React from 'react'
import { theme } from '../../theme'
import { Loader2 } from 'lucide-react'

export interface ButtonProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'size'> {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger'
  size?: 'sm' | 'md' | 'lg'
  leftIcon?: React.ReactNode
  rightIcon?: React.ReactNode
  loading?: boolean
  danger?: boolean
  children: React.ReactNode
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(({
  variant: variantProp,
  size = 'md',
  leftIcon,
  rightIcon,
  loading = false,
  danger,
  children,
  disabled,
  ...props
}, ref) => {
  // If danger prop is true, override variant
  const variant = danger ? 'danger' : (variantProp || 'primary');
  const baseStyle: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing.xs,
    border: 'none',
    borderRadius: theme.borderRadius.md,
    cursor: disabled ? 'not-allowed' : 'pointer',
    transition: `all ${theme.animations.duration.fast}ms`,
    fontFamily: theme.typography.fontFamily.primary,
    fontWeight: 600,
    opacity: disabled ? 0.5 : 1,
  }

  const sizeStyles: Record<string, React.CSSProperties> = {
    sm: {
      padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
      fontSize: theme.typography.body.fontSize,
      height: '32px',
    },
    md: {
      padding: `${theme.spacing.sm} ${theme.spacing.md}`,
      fontSize: theme.typography.body.fontSize,
      height: '40px',
    },
    lg: {
      padding: `${theme.spacing.md} ${theme.spacing.lg}`,
      fontSize: '15px',
      height: '48px',
    },
  }

  const variantStyles: Record<string, React.CSSProperties> = {
    primary: {
      backgroundColor: theme.colors.primary,
      color: '#FFFFFF',
    },
    secondary: {
      backgroundColor: theme.colors.surface,
      color: theme.colors.text.primary,
      border: `1px solid ${theme.colors.border}`,
    },
    ghost: {
      backgroundColor: 'transparent',
      color: theme.colors.text.secondary,
    },
    danger: {
      backgroundColor: theme.colors.error,
      color: '#FFFFFF',
    },
  }

  return (
    <button
      ref={ref}
      style={{
        ...baseStyle,
        ...sizeStyles[size],
        ...variantStyles[variant],
      }}
      disabled={disabled || loading}
      {...props}
    >
      {loading && (
        <Loader2
          size={size === 'sm' ? 14 : size === 'lg' ? 18 : 16}
          style={{ animation: 'spin 1s linear infinite' }}
        />
      )}
      {!loading && leftIcon && leftIcon}
      {children}
      {!loading && rightIcon && rightIcon}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </button>
  )
})

Button.displayName = 'Button'
